import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import PropTypes$1 from 'prop-types';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { v1 } from 'uuid';
import { PropTypes } from '@micromag/core';
import { Button, PlayIcon, PauseIcon, ScreenElement, Transitions } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef, useScreenRenderContext } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver } from '@micromag/core/hooks';
import { getStyleFromColor, isHeaderFilled, isFooterFilled, getFooterProps, isTextFilled } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout from '@micromag/element-layout';
import Scroll from '@micromag/element-scroll';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';
import Audio from '@micromag/element-audio';

var styles = {"container":"micromag-screen-conversation-container","background":"micromag-screen-conversation-background","disabled":"micromag-screen-conversation-disabled","hidden":"micromag-screen-conversation-hidden","placeholder":"micromag-screen-conversation-placeholder","content":"micromag-screen-conversation-content","conversationHeader":"micromag-screen-conversation-conversationHeader","empty":"micromag-screen-conversation-empty","title":"micromag-screen-conversation-title","footer":"micromag-screen-conversation-footer","layout":"micromag-screen-conversation-layout","messageContainer":"micromag-screen-conversation-messageContainer","message":"micromag-screen-conversation-message","normalLeft":"micromag-screen-conversation-normalLeft","inBetweenLeft":"micromag-screen-conversation-inBetweenLeft","normalRight":"micromag-screen-conversation-normalRight","inBetweenRight":"micromag-screen-conversation-inBetweenRight","last":"micromag-screen-conversation-last","speakerDetails":"micromag-screen-conversation-speakerDetails","right":"micromag-screen-conversation-right","avatarContainer":"micromag-screen-conversation-avatarContainer","avatar":"micromag-screen-conversation-avatar","imageContainer":"micromag-screen-conversation-imageContainer","withAnimation":"micromag-screen-conversation-withAnimation","appear":"micromag-screen-conversation-appear","loadingContainer":"micromag-screen-conversation-loadingContainer","loading":"micromag-screen-conversation-loading","dot":"micromag-screen-conversation-dot","bounce":"micromag-screen-conversation-bounce","loadingSpeakerName":"micromag-screen-conversation-loadingSpeakerName","audioMessageContainer":"micromag-screen-conversation-audioMessageContainer","playButtonContainer":"micromag-screen-conversation-playButtonContainer","audioMessage":"micromag-screen-conversation-audioMessage"};

var propTypes$2 = {
  audio: PropTypes.audioMedia,
  audioEventsChannelName: PropTypes$1.string,
  messageId: PropTypes$1.string,
  nextAudioMessageId: PropTypes$1.string
};
var defaultProps$2 = {
  audio: null,
  audioEventsChannelName: null,
  messageId: null,
  nextAudioMessageId: null
};
var ConversationAudioAttachment = function ConversationAudioAttachment(_ref) {
  var audio = _ref.audio,
    audioEventsChannelName = _ref.audioEventsChannelName,
    messageId = _ref.messageId,
    nextAudioMessageId = _ref.nextAudioMessageId;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    paused = _useState2[0],
    setPaused = _useState2[1];
  var audioEventsChannel = useMemo(function () {
    return new BroadcastChannel(audioEventsChannelName);
  }, [audioEventsChannelName]);
  useEffect(function () {
    var onMessage = function onMessage(message) {
      var _message$data = message.data,
        type = _message$data.type,
        id = _message$data.id;
      switch (type) {
        case 'play':
          {
            setPaused(id !== messageId);
          }
      }
    };
    audioEventsChannel.addEventListener("message", onMessage);
    return function () {
      return audioEventsChannel.removeEventListener("message", onMessage);
    };
  }, [audioEventsChannel]);
  var togglePaused = function togglePaused() {
    if (paused) {
      setPaused(false);
      audioEventsChannel.postMessage({
        type: 'play',
        id: messageId
      });
    } else {
      setPaused(true);
    }
  };
  var onAudioEnded = function onAudioEnded() {
    if (nextAudioMessageId) {
      audioEventsChannel.postMessage({
        type: 'play',
        id: nextAudioMessageId
      });
    }
    setPaused(true);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.audioMessageContainer])
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.playButtonContainer
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: togglePaused,
    icon: paused ? /*#__PURE__*/React.createElement(PlayIcon, null) : /*#__PURE__*/React.createElement(PauseIcon, null)
  })), /*#__PURE__*/React.createElement(Audio, {
    withWave: true,
    autoWaveHeight: true,
    className: styles.audioMessage,
    media: audio,
    paused: paused,
    updateInterval: 100,
    onEnded: onAudioEnded
  }));
};
ConversationAudioAttachment.propTypes = propTypes$2;
ConversationAudioAttachment.defaultProps = defaultProps$2;

var propTypes$1 = {
  message: PropTypes.conversationMessage,
  messageId: PropTypes$1.string,
  previousMessage: PropTypes.conversationMessage,
  nextMessage: PropTypes.conversationMessage,
  nextAudioMessageId: PropTypes$1.string,
  nextMessageState: PropTypes$1.bool,
  currentSpeaker: PropTypes.speaker,
  // state: PropTypes.oneOf(['pause', 'typing', 'send']),
  conversationTiming: PropTypes$1.number,
  typingTiming: PropTypes$1.number,
  onChange: PropTypes$1.func,
  withAnimation: PropTypes$1.bool,
  active: PropTypes$1.bool,
  isPlaying: PropTypes$1.bool,
  speakerStyle: PropTypes.textStyle,
  messageStyle: PropTypes.textStyle,
  className: PropTypes$1.string,
  audioEventsChannelName: PropTypes$1.string
};
var defaultProps$1 = {
  message: null,
  messageId: null,
  previousMessage: null,
  nextMessage: null,
  nextAudioMessageId: null,
  nextMessageState: null,
  currentSpeaker: null,
  conversationTiming: null,
  typingTiming: null,
  onChange: null,
  withAnimation: false,
  active: false,
  isPlaying: false,
  messageStyle: null,
  speakerStyle: null,
  className: null,
  audioEventsChannelName: null
};
var ConversationMessage = function ConversationMessage(_ref) {
  var message = _ref.message,
    messageId = _ref.messageId,
    previousMessage = _ref.previousMessage,
    nextMessage = _ref.nextMessage,
    nextAudioMessageId = _ref.nextAudioMessageId,
    nextMessageState = _ref.nextMessageState,
    currentSpeaker = _ref.currentSpeaker,
    conversationTiming = _ref.conversationTiming,
    typingTiming = _ref.typingTiming,
    onChange = _ref.onChange,
    withAnimation = _ref.withAnimation,
    active = _ref.active,
    isPlaying = _ref.isPlaying,
    messageStyle = _ref.messageStyle,
    speakerStyle = _ref.speakerStyle,
    className = _ref.className,
    audioEventsChannelName = _ref.audioEventsChannelName;
  var _ref2 = message || {},
    messageBody = _ref2.message,
    _ref2$image = _ref2.image,
    image = _ref2$image === void 0 ? null : _ref2$image,
    audio = _ref2.audio;
  var _ref3 = currentSpeaker || {},
    _ref3$avatar = _ref3.avatar,
    _ref3$avatar2 = _ref3$avatar === void 0 ? {} : _ref3$avatar,
    _ref3$avatar2$url = _ref3$avatar2.url,
    avatarUrl = _ref3$avatar2$url === void 0 ? null : _ref3$avatar2$url,
    speakerName = _ref3.name,
    _ref3$side = _ref3.side,
    side = _ref3$side === void 0 ? 'left' : _ref3$side,
    currentSpeakerId = _ref3.id,
    color = _ref3.color;
  var right = side === 'right';
  var isPrevSpeakerTheSame = previousMessage !== null && previousMessage.speaker === currentSpeakerId;
  var isNextSpeakerTheSame = nextMessage !== null && nextMessage.speaker === currentSpeakerId;

  // Timing
  var _useState = useState(withAnimation ? 'pause' : 'send'),
    _useState2 = _slicedToArray(_useState, 2),
    messageState = _useState2[0],
    setMessageState = _useState2[1];
  var pauseBeforeTyping = conversationTiming;
  var typingDuration = typingTiming;
  useEffect(function () {
    if (!withAnimation || !isPlaying) {
      return function () {};
    }
    var timeout = null;
    if (messageState === 'pause') {
      timeout = setTimeout(function () {
        return setMessageState('typing');
      }, pauseBeforeTyping);
    } else if (messageState === 'typing') {
      timeout = setTimeout(function () {
        return setMessageState('send');
      }, typingDuration);
    }
    return function () {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
  }, [withAnimation, isPlaying, messageState, setMessageState, pauseBeforeTyping, typingDuration]);
  useEffect(function () {
    if (messageState !== 'pause' && onChange !== null) {
      onChange(messageState);
    }
  }, [messageState]);
  var betweenStyle = isNextSpeakerTheSame && nextMessageState;
  return messageState !== 'pause' ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.messageContainer, _defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles.withAnimation, withAnimation === true), styles.right, right)])
  }, messageState === 'typing' ? /*#__PURE__*/React.createElement("div", {
    className: styles.loadingContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.loading
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.loadingSpeakerName
  }, speakerName)) : /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.message, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, styles.normalRight, right), styles.nextTheSame, isNextSpeakerTheSame === true && isPrevSpeakerTheSame), styles.inBetweenRight, betweenStyle && right), styles.normalLeft, !right), styles.inBetweenLeft, betweenStyle && !right), styles.last, isNextSpeakerTheSame === false)]),
    style: _objectSpread({}, getStyleFromColor(color))
  }, !isPrevSpeakerTheSame ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.speakerDetails, _defineProperty({}, styles.right, side === 'right')])
  }, avatarUrl !== null ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.avatarContainer, _defineProperty({}, styles.right, side === 'right')])
  }, /*#__PURE__*/React.createElement("img", {
    className: styles.avatar,
    src: avatarUrl,
    alt: speakerName,
    loading: "lazy"
  })) : null, /*#__PURE__*/React.createElement(Text, {
    body: speakerName,
    textStyle: speakerStyle
  })) : null, /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", {
    className: styles.messageBody
  }, image !== null ? /*#__PURE__*/React.createElement("div", {
    className: styles.imageContainer
  }, /*#__PURE__*/React.createElement(Visual, {
    media: image,
    width: "100%",
    playing: isPlaying,
    active: active
  })) : null, /*#__PURE__*/React.createElement(Text, {
    className: styles.messageBody,
    body: messageBody,
    textStyle: messageStyle
  }), audio ? /*#__PURE__*/React.createElement(ConversationAudioAttachment, {
    audio: audio,
    messageId: messageId,
    nextAudioMessageId: nextAudioMessageId,
    audioEventsChannelName: audioEventsChannelName
  }) : null))) : null;
};
ConversationMessage.propTypes = propTypes$1;
ConversationMessage.defaultProps = defaultProps$1;

var propTypes = {
  // id: PropTypes.string,
  // layout: PropTypes.oneOf(['normal']),
  title: PropTypes.headingElement,
  timing: PropTypes$1.oneOf(['instant', 'sequence']),
  readingSpeed: PropTypes$1.number,
  spacing: PropTypes$1.number,
  background: PropTypes.backgroundElement,
  header: PropTypes.header,
  footer: PropTypes.footer,
  current: PropTypes$1.bool,
  active: PropTypes$1.bool,
  type: PropTypes$1.string,
  conversation: PropTypes.conversation,
  transitions: PropTypes.transitions,
  className: PropTypes$1.string
};
var defaultProps = {
  // layout: 'normal',
  title: null,
  timing: 'sequence',
  readingSpeed: 255,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  current: true,
  active: true,
  type: null,
  conversation: null,
  transitions: null,
  className: null
};
var ConversationScreen = function ConversationScreen(_ref) {
  var title = _ref.title,
    timingMode = _ref.timing,
    readingSpeed = _ref.readingSpeed,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    active = _ref.active,
    type = _ref.type,
    conversation = _ref.conversation,
    transitions = _ref.transitions,
    className = _ref.className;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var trackScreenEvent = useTrackScreenEvent(type);
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var audioEventsChannel = new BroadcastChannel("conversation_".concat(v1(), "_audioEvents"));
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || active;
  var withAnimation = isView && !isStatic && timingMode === 'sequence';
  var _ref2 = conversation || {},
    _ref2$speakers = _ref2.speakers,
    speakers = _ref2$speakers === void 0 ? null : _ref2$speakers,
    _ref2$messages = _ref2.messages,
    messages = _ref2$messages === void 0 ? [] : _ref2$messages,
    messageStyle = _ref2.messageStyle,
    speakerStyle = _ref2.speakerStyle;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    conversationState = _useState2[0],
    setConversationState = _useState2[1];
  var chatBottomRef = useRef(null);
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var hasTitle = isTextFilled(title);
  var _useDimensionObserver = useDimensionObserver(),
    contentRef = _useDimensionObserver.ref,
    scrollHeight = _useDimensionObserver.height;
  var scrollRef = useRef(null);
  useEffect(function () {
    if (withAnimation && scrollRef.current !== null) {
      scrollRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [scrollHeight, withAnimation]);
  var animationFinished = messages.length === conversationState.length;
  var conversationStateChange = useCallback(function (state) {
    var newConversationState = _toConsumableArray(conversationState);
    if (state === 'send') {
      newConversationState.push(true);
      setConversationState(newConversationState);
    }
  }, [conversationState, setConversationState]);

  // sequence timings
  var defaultHesitationDelay = 1500;
  var imageReadDelay = 5000; // 5 seconds
  var millisecondsPerWord = 60 * 1000 / readingSpeed;
  var filteredMessages = (messages || []).filter(function (m) {
    return m !== null;
  });
  var timings = filteredMessages.map(function (messageParams, messageIndex) {
    if (messageIndex === 0) {
      return 0;
    }
    var _ref3 = messageParams || {},
      _ref3$timing = _ref3.timing,
      timing = _ref3$timing === void 0 ? null : _ref3$timing,
      _ref3$message = _ref3.message,
      message = _ref3$message === void 0 ? null : _ref3$message,
      image = _ref3.image,
      audio = _ref3.audio,
      timingOverrides = _ref3.timingOverrides;
    if (timing !== null) {
      return timing;
    }
    if (timingOverrides !== null && timingOverrides !== void 0 && timingOverrides.enabled && Number.isFinite(timingOverrides === null || timingOverrides === void 0 ? void 0 : timingOverrides.writingDuration)) {
      return timingOverrides.writingDuration * 1000; // seconds to milliseconds
    }

    // if the current message has an audio attachment, use the time it takes to record that message
    if (audio) {
      return audio.metadata.duration;
    }

    // counting words: only keep whitespaces and alphanumeric characters, then split of whitespaces
    var wordCount = message ? message.replace(/[^\w\d\s]/g, '').trim().split(/\s/g).length : 0;
    var finalTimeMs = wordCount * millisecondsPerWord;

    // if the message includes an image, add some more time to "read" it
    if (image) {
      finalTimeMs += imageReadDelay;
    }
    return finalTimeMs;
  });
  var hesitationTimings = filteredMessages.map(function (messageParams, messageIndex) {
    var timingOverrides = messageParams.timingOverrides;
    if (messageIndex !== 0 && timingOverrides !== null && timingOverrides !== void 0 && timingOverrides.enabled && Number.isFinite(timingOverrides === null || timingOverrides === void 0 ? void 0 : timingOverrides.appearDelay)) {
      return timingOverrides.appearDelay * 1000; // seconds to milliseconds
    }

    return defaultHesitationDelay;
  });
  var messagesUniqueId = useMemo(function () {
    return (messages || []).map(function () {
      return v1();
    });
  }, [messages]);

  // scroll
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;
  var scrollingDisabled = !isEdit && transitionDisabled || !current;
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    scrolledBottom = _useState4[0],
    setScrolledBottom = _useState4[1];
  var showFooter = animationFinished && !isPlaceholder && hasFooter || !withAnimation;
  var onScrolledBottom = useCallback(function (_ref4) {
    var initial = _ref4.initial;
    if (initial) {
      trackScreenEvent('scroll', 'Screen');
    }
    setScrolledBottom(true);
  }, [trackScreenEvent]);
  var onScrolledNotBottom = useCallback(function () {
    setScrolledBottom(false);
  }, [setScrolledBottom]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": true
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Scroll, {
    ref: scrollRef,
    disabled: scrollingDisabled,
    onScrolledBottom: onScrolledBottom,
    onScrolledNotBottom: onScrolledNotBottom,
    withShadow: true
  }, /*#__PURE__*/React.createElement("div", {
    ref: contentRef
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (!isPreview ? viewerTopHeight : 0) + spacing / 2,
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + spacing / 2
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    key: "header",
    style: {
      paddingBottom: spacing
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "conversation",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "NmCfTO",
      defaultMessage: [{
        "type": 0,
        "value": "Conversation"
      }]
    }),
    emptyClassName: styles.empty,
    isEmpty: messages.length === 0 && title === null
  }, /*#__PURE__*/React.createElement(Transitions, {
    transitions: transitions,
    playing: current,
    disabled: transitionDisabled
  }, hasTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({}, title, {
    className: styles.title,
    isEmpty: title === null
  })) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.conversation
  }, filteredMessages.map(function (m, messageI) {
    var previousMessage = messageI !== 0 ? messages[messageI - 1] : null;
    var nextMessage = messageI + 1 < messages.length ? messages[messageI + 1] : null;
    var speaker = m.speaker;
    var currentSpeaker = (speakers || []).find(function (s) {
      return s.id === speaker;
    }) || null;
    var shouldPlay = messageI === 0 || conversationState[messageI - 1] === true;
    var pauseTiming = hesitationTimings[messageI];
    var typingTiming = timings[messageI];
    var messageId = "".concat(m.message, "-").concat(messagesUniqueId[messageI]);
    var nextAudioMessage = filteredMessages.slice(messageI + 1).find(function (c) {
      return c.audio != null;
    });
    var nextAudioMessageId = nextAudioMessage ? "".concat(m.message, "-").concat(messagesUniqueId[filteredMessages.indexOf(nextAudioMessage)]) : null;
    return /*#__PURE__*/React.createElement(ConversationMessage, {
      key: messageId,
      message: m,
      messageId: messageId,
      previousMessage: previousMessage,
      nextMessage: nextMessage,
      nextAudioMessageId: nextAudioMessageId,
      nextMessageState: conversationState[messageI + 1] || !withAnimation,
      currentSpeaker: currentSpeaker,
      conversationTiming: pauseTiming,
      typingTiming: typingTiming,
      onChange: conversationStateChange,
      withAnimation: withAnimation,
      isPlaying: current && shouldPlay,
      shouldLoad: mediaShouldLoad,
      withoutVideo: isPreview,
      messageStyle: messageStyle,
      speakerStyle: speakerStyle,
      audioEventsChannelName: audioEventsChannel.name
    });
  })), showFooter ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.footer, _defineProperty({}, styles.disabled, !scrolledBottom)]),
    style: {
      paddingLeft: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingRight: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingTop: spacing
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null, /*#__PURE__*/React.createElement("div", {
    ref: chatBottomRef
  }))))))), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
ConversationScreen.propTypes = propTypes;
ConversationScreen.defaultProps = defaultProps;

var definition = {
  id: 'conversation',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "fIawTr",
      defaultMessage: [{
        "type": 0,
        "value": "Text"
      }]
    }),
    order: 3
  },
  title: defineMessage({
    id: "rBPIgw",
    defaultMessage: [{
      "type": 0,
      "value": "Conversation"
    }]
  }),
  component: ConversationScreen,
  layouts: ['normal'],
  fields: [
  // {
  //     name: 'theme',
  //     type: 'select',
  //     options: [
  //         {
  //             value: 'ios',
  //             label: 'iOS',
  //         },
  //     ],
  //     label: defineMessage({
  //         defaultMessage: 'Theme',
  //         description: 'Theme field label',
  //     }),
  // },
  {
    name: 'timing',
    type: 'select',
    options: [{
      value: 'instant',
      label: 'Instant'
    }, {
      value: 'sequence',
      label: 'Sequence'
    }],
    label: defineMessage({
      id: "6DV50M",
      defaultMessage: [{
        "type": 0,
        "value": "Timing"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'conversation',
    type: 'conversation',
    label: defineMessage({
      id: "8tANs8",
      defaultMessage: [{
        "type": 0,
        "value": "Conversation"
      }]
    })
  }, {
    name: 'readingSpeed',
    type: 'number',
    defaultValue: 255,
    label: defineMessage({
      id: "QjbLZ9",
      defaultMessage: [{
        "type": 0,
        "value": "Reading speed (in Words Per Minute)"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { ConversationScreen, definition as default };
